// =====================================================================
// PatientToBoardDialog (Quadros PRO / S60) — "virar tarefa" a partir de um
// PACIENTE: cria um card já vinculado ao paciente. Self-contained (aditivo).
// =====================================================================
import React, { useState, useEffect } from "react";
import { useHistory } from "react-router-dom";
import {
  Dialog, DialogTitle, DialogContent, DialogActions, Button, TextField, MenuItem, Box, Typography, CircularProgress
} from "@material-ui/core";
import { toast } from "react-toastify";
import { listBoards, getBoard, createCardFromPatient, boardErrorMessage } from "../../services/boardService";

const PatientToBoardDialog = ({ open, onClose, patient }) => {
  const history = useHistory();
  const [boards, setBoards] = useState([]);
  const [boardId, setBoardId] = useState("");
  const [lists, setLists] = useState([]);
  const [listId, setListId] = useState("");
  const [title, setTitle] = useState("");
  const [loadingBoards, setLoadingBoards] = useState(false);
  const [loadingLists, setLoadingLists] = useState(false);
  const [saving, setSaving] = useState(false);

  const patientName = patient ? patient.fullName : "";

  useEffect(() => {
    if (!open) return;
    setTitle(patientName ? `Paciente: ${patientName}` : `Paciente #${(patient && patient.id) || ""}`);
    setBoardId(""); setLists([]); setListId("");
    (async () => {
      setLoadingBoards(true);
      try {
        const d = await listBoards();
        const bs = (d && d.boards) || [];
        setBoards(bs);
        if (bs.length > 0) setBoardId(String(bs[0].id));
      } catch (e) { toast.error(boardErrorMessage(e)); }
      finally { setLoadingBoards(false); }
    })();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open]);

  useEffect(() => {
    if (!open || !boardId) { setLists([]); setListId(""); return; }
    (async () => {
      setLoadingLists(true);
      try {
        const b = await getBoard(boardId);
        const ls = (b && b.lists) || [];
        setLists(ls);
        setListId(ls.length > 0 ? String(ls[0].id) : "");
      } catch { setLists([]); setListId(""); }
      finally { setLoadingLists(false); }
    })();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [boardId, open]);

  const handleCreate = async () => {
    if (!listId) { toast.error("Escolha um quadro e uma coluna."); return; }
    setSaving(true);
    try {
      await createCardFromPatient({ patientId: patient.id, listId: Number(listId), title: title.trim() });
      toast.success("Tarefa criada no Quadros.");
      onClose && onClose();
      if (boardId) history.push(`/quadros/${boardId}`);
    } catch (e) { toast.error(boardErrorMessage(e)); }
    finally { setSaving(false); }
  };

  return (
    <Dialog open={open} onClose={saving ? undefined : onClose} maxWidth="xs" fullWidth>
      <DialogTitle>Criar tarefa no Quadros</DialogTitle>
      <DialogContent dividers>
        {loadingBoards ? (
          <Box display="flex" justifyContent="center" p={2}><CircularProgress size={22} /></Box>
        ) : boards.length === 0 ? (
          <Typography variant="body2" color="textSecondary">
            Nenhum quadro ainda. Crie um quadro em "Quadros" primeiro.
          </Typography>
        ) : (
          <>
            <TextField select fullWidth margin="dense" label="Quadro" value={boardId} onChange={(e) => setBoardId(e.target.value)}>
              {boards.map((b) => <MenuItem key={b.id} value={String(b.id)}>{b.name}</MenuItem>)}
            </TextField>
            <TextField select fullWidth margin="dense" label="Coluna" value={listId} onChange={(e) => setListId(e.target.value)}
              disabled={loadingLists || lists.length === 0}
              helperText={!loadingLists && lists.length === 0 ? "Este quadro não tem colunas." : ""}>
              {lists.map((l) => <MenuItem key={l.id} value={String(l.id)}>{l.name}</MenuItem>)}
            </TextField>
            <TextField fullWidth margin="dense" label="Título do card" value={title} onChange={(e) => setTitle(e.target.value)} />
            <Typography variant="caption" color="textSecondary">
              O card será vinculado a este paciente{patientName ? ` (${patientName})` : ""} automaticamente.
            </Typography>
          </>
        )}
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose} disabled={saving}>Cancelar</Button>
        <Button variant="contained" color="primary" onClick={handleCreate} disabled={saving || loadingBoards || !listId}
          startIcon={saving ? <CircularProgress size={16} /> : null}>Criar tarefa</Button>
      </DialogActions>
    </Dialog>
  );
};

export default PatientToBoardDialog;
